import { ref } from 'vue';
import { useApi } from '@/composables/useApi.ts';
import { cloneDeep } from '@/composables/useUtils.ts';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import type { Signee, SigneeSigning } from '@/types/Signee.ts';
import type { AutographPosition } from '@/types/AutographPosition.ts';
import type { Document } from '@/types/Document.ts';
import {
  createDocumentSignee,
  updateDocumentSignee
} from '@/api/deepsign/documents-signees.ts';
import {
  buildFileAutographPositionsFromSignings,
  buildFileSignatureFieldsFromSignings,
  findSigneeGroupForSignee,
  getDefaultSigneeFileId,
  isSigneeGroupQuorumSet,
  persistSigneeGroupSignatureFields
} from '@/utils/signeeSignings.ts';

export const DRAFT_SIGNEE_PREFIX = 'draft-';

export function createDraftSigneeId(): string {
  return `${DRAFT_SIGNEE_PREFIX}${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 9)}`;
}

export function isDraftSigneeId(signeeId: string | undefined | null): boolean {
  return !!signeeId && signeeId.startsWith(DRAFT_SIGNEE_PREFIX);
}

/**
 * Draft lifecycle + persistence layer for the signing-workflow dialog's
 * participants step.
 *
 * On open we clone `document.signeesOrdered` into `createStore.draftSigneesOrdered`
 * and the editing UI mutates that draft only (see `createStore.deferPersistence`
 * and the working-set `signeesOrdered` computed). The real document stays the
 * committed source of truth. On "Confirm" we diff the draft against the document
 * and issue the minimal set of backend calls, then commit the draft into the
 * document. On "Cancel" we simply drop the draft — the document was never
 * touched.
 */
export function useParticipantsReconcile(store) {
  const createStore = store;
  const { deepSignAPI } = useApi();

  const orderedBaseline = ref(false);
  // Snapshot of the real (server-persisted) signee ids present when the dialog
  // opened — the document's "initial payload". Reconcile deletes any of these
  // that the final draft dropped, so a signee removed locally (e.g. toggling
  // "I am signing" off) is actually deleted on the backend before the sign-order
  // call. Captured at open so it can't be thrown off by later edits to the
  // committed list.
  const initialSigneeIds = ref<Set<string>>(new Set());

  function realSigneeIds(groups: Array<Array<Signee>>): Set<string> {
    return new Set(
      groups
        .flat()
        .map((signee) => signee.signeeId)
        .filter((signeeId) => !isDraftSigneeId(signeeId))
    );
  }

  // The document is grouped/sequenced only with 2+ non-empty groups (mirrors
  // OrderInviteesSwitch.vue's onBeforeMount rule) — a single group, however it
  // was created, is unordered.
  function isOrderedGrouping(groups: Array<Array<Signee>>): boolean {
    return groups.filter((group) => group.length > 0).length > 1;
  }

  // The signees the backend actually has right now. The open-time snapshot
  // (`initialSigneeIds`) and the committed document are both client state and
  // can be out of sync with the server when the dialog lost its draft/deferred
  // mode mid-session (the create page resets the store and re-fetches the
  // document shortly after mount, which is what the DeepBox entry path hits:
  // the document isn't loaded yet when the page mounts). In that state the X
  // button drops a signee from local state only and the snapshot never saw it,
  // so a purely local diff would silently skip its DELETE and the removed
  // signee — typically the auto-added initiator — would still be invited.
  // Asking the backend once per Confirm makes the deletion diff authoritative:
  // the participant list shown in the dialog is the complete intended list.
  async function serverSigneeIds(documentId: string): Promise<Set<string>> {
    try {
      const response = await deepSignAPI.value.documents.getById(documentId);
      return realSigneeIds(
        (response.data?.signeesOrdered ?? []) as Array<Array<Signee>>
      );
    } catch (e) {
      console.error('Failed to read the persisted signees', e);
      return new Set();
    }
  }

  function beginParticipantsDraft() {
    // Always create the draft, even when the dialog opens before the document
    // is set (fresh envelope): a null draft would silently route every edit
    // into the committed document and make reconcile a no-op on Confirm,
    // leaving draft- ids that were never persisted.
    const committedGroups = createStore.document?.signeesOrdered ?? [];
    createStore.draftSigneesOrdered = cloneDeep(committedGroups);
    // Derive the baseline straight from the document's actual grouping rather
    // than trusting createStore.ordered: the dialog's child components (e.g.
    // OrderInviteesSwitch.vue) only correct that ref on their own mount, which
    // happens AFTER this runs, so on a fresh store it can still hold its
    // stale/default value here. Capturing a wrong baseline makes the Confirm
    // handler's orderedChanged check silently see "no change" even after the
    // user toggled the switch, skipping the sign-order backend call entirely.
    const canonicalOrdered = isOrderedGrouping(committedGroups);
    createStore.ordered = canonicalOrdered;
    orderedBaseline.value = canonicalOrdered;
    initialSigneeIds.value = realSigneeIds(committedGroups);
  }

  function discardParticipantsDraft() {
    createStore.draftSigneesOrdered = null;
    createStore.ordered = orderedBaseline.value;
    initialSigneeIds.value = new Set();
  }

  // True while the draft holds nothing the user could lose: no persisted signee
  // and no e-mail typed into a locally added one. The dialog uses this to tell
  // an untouched draft apart from real edits, so it can rebuild it when the
  // document it was cloned from arrives late (the dialog can open before the
  // document finished loading — then the draft is empty and the participants
  // step shows a blank field instead of the signees the document already has).
  function isParticipantsDraftDiscardable(): boolean {
    const draft = createStore.draftSigneesOrdered as Array<
      Array<Signee>
    > | null;
    if (!draft) return true;
    return draft
      .flat()
      .every((signee) => isDraftSigneeId(signee.signeeId) && !signee.email);
  }

  function autographPositionChanged(
    a: AutographPosition | null,
    b: AutographPosition | null
  ): boolean {
    return JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);
  }

  function signingsChanged(
    a: SigneeSigning[] | undefined | null,
    b: SigneeSigning[] | undefined | null
  ): boolean {
    return JSON.stringify(a ?? []) !== JSON.stringify(b ?? []);
  }

  function groupingPayload(groups: Array<Array<Signee>>): string[][] {
    return groups
      .filter((group) => group.length > 0)
      .map((group) => group.map((signee) => signee.signeeId));
  }

  // Persists an existing signee's email/comment/autograph changes. Once the
  // signee's group already has a quorum (requiredSignatures > 0), its
  // positions are shared, group-owned signature slots — persist email/comment
  // as usual and push the positions through updateSigneeGroupSignatureField
  // instead of the signee endpoint (which 403s on autograph fields).
  async function persistSigneeUpdate(
    documentId: string,
    doc: Document | null | undefined,
    signee: Signee,
    hasAutographUpdate: boolean
  ) {
    const signeeGroup = findSigneeGroupForSignee(
      signee.signeeId,
      doc?.signeeGroups
    );
    const isQuorumGroup =
      hasAutographUpdate && isSigneeGroupQuorumSet(signeeGroup);

    if (isQuorumGroup && signeeGroup) {
      await updateDocumentSignee(documentId, signee.signeeId, {
        email: signee.email ?? null,
        comment: signee.signeeComment ?? null
      });

      const defaultFileId = getDefaultSigneeFileId(doc);
      const fileAutographPositions =
        buildFileAutographPositionsFromSignings(signee.signings) ??
        (signee.autographPosition !== undefined && defaultFileId
          ? {
              [defaultFileId]: signee.autographPosition
                ? [signee.autographPosition]
                : []
            }
          : {});

      await persistSigneeGroupSignatureFields(
        documentId,
        signeeGroup,
        signee.signeeId,
        fileAutographPositions
      );
      return;
    }

    await updateDocumentSignee(documentId, signee.signeeId, {
      ...(signee.autographPosition !== undefined &&
        hasAutographUpdate && {
          autographPosition: signee.autographPosition
        }),
      ...(buildFileAutographPositionsFromSignings(signee.signings) && {
        fileAutographPositions: buildFileAutographPositionsFromSignings(
          signee.signings
        )
      }),
      email: signee.email ?? null,
      comment: signee.signeeComment ?? null
    });
  }

  // The creates/deletes in reconcileParticipants only touch `signeesOrdered`
  // (via the committed draft); they never update `doc.signeeGroups`, which is
  // the authoritative source for the sidebar's per-file missing-signature badge
  // and the placement rebuild. Left stale, a removed signee lingers in
  // signeeGroups as a phantom that always reads as "unplaced" — the badge then
  // shows even when every remaining signee is placed — and a newly added signee
  // is absent from it entirely. Re-fetch it whenever membership changed, the
  // same way file mutations already refresh it (refreshDocumentFiles).
  async function refreshSigneeGroupsAfterMembershipChange(
    addedSigneeIds: string[],
    removedSigneeIds: string[]
  ) {
    if (removedSigneeIds.length === 0 && addedSigneeIds.length === 0) return;
    await createStore.refreshSigneeGroups();
  }

  async function reconcileParticipants() {
    const doc = createStore.document;
    if (!doc) return { addedSigneeIds: [], removedSigneeIds: [] };
    // Normally the dialog edits the draft clone. If the draft is missing (a
    // broken open sequence or a store reset while the dialog was open), fall
    // back to the committed list itself: any signee still carrying a draft-
    // id in there was never created server-side, and the diff below creates
    // it — and swaps in the real id — exactly as it would for a draft entry.
    const draft = (createStore.draftSigneesOrdered ??
      doc.signeesOrdered) as Array<Array<Signee>>;

    const documentId = doc.documentId;
    // The committed document is the baseline; the draft is the desired state.
    const baseline = doc.signeesOrdered as Array<Array<Signee>>;
    const baselineFlat = baseline.flat();
    const draftFlat = draft.flat();

    // 1. Removes — persisted signees that are no longer in the draft. We diff
    // against the union of the open-time snapshot (`initialSigneeIds`) and the
    // signees the backend currently has, never against the live committed list:
    // a signee removed locally (toggling "I am signing" off, the X button)
    // leaves the draft, and if the committed list no longer reflects it either,
    // a live diff misses the deletion — the backend keeps the signee and the
    // sign-order call below is then rejected ("Missing signeeId …") because the
    // payload omits a signee the document still has. The server side of the
    // union additionally covers a snapshot that never saw the signee (dialog
    // opened before the document finished loading, or the draft torn down by a
    // store reset). Deleting here, before the sign-order update, keeps the
    // backend and the payload in sync.
    const desiredRealIds = new Set(
      draftFlat
        .filter((signee: Signee) => !isDraftSigneeId(signee.signeeId))
        .map((signee: Signee) => signee.signeeId)
    );
    const removedSigneeIds: string[] = [];
    const persistedSigneeIds = new Set<string>([
      ...initialSigneeIds.value,
      ...(await serverSigneeIds(documentId))
    ]);
    for (const signeeId of persistedSigneeIds) {
      if (!desiredRealIds.has(signeeId)) {
        await deepSignAPI.value.documentsSignees.deleteById(
          documentId,
          signeeId
        );
        removedSigneeIds.push(signeeId);
      }
    }

    // 2. Adds — signees created locally (temp ids). Swap the temp id for the
    // persisted one in place so the placement watcher re-keys placeholders to
    // the real signeeId (the signee object keeps its policy/position). The real
    // ids of freshly created signees are collected so callers can auto-place a
    // placeholder for them on the selected file.
    const addedSigneeIds: string[] = [];
    for (const signee of draftFlat) {
      if (!isDraftSigneeId(signee.signeeId)) continue;
      const response = await createDocumentSignee(documentId, {
        email: signee.email ?? undefined,
        comment: signee.signeeComment || '',
        signOrder: signee.signOrder || 0,
        autographPosition: signee.autographPosition ?? undefined,
        signeeType: signee.signeeType || SigneeType.SIGN,
        ...(buildFileSignatureFieldsFromSignings(signee.signings) && {
          fileSignatureFields: buildFileSignatureFieldsFromSignings(
            signee.signings
          )
        })
      });
      if (response.data?.signeeId) {
        signee.signeeId = response.data.signeeId;
        addedSigneeIds.push(response.data.signeeId);
      }
    }

    // 3. Updates — existing signees whose email or autograph position changed.
    for (const signee of draftFlat) {
      if (isDraftSigneeId(signee.signeeId)) continue;
      const base = baselineFlat.find((b) => b.signeeId === signee.signeeId);
      if (!base) continue;
      const emailChanged = (base.email ?? null) !== (signee.email ?? null);
      const positionChanged = autographPositionChanged(
        base.autographPosition,
        signee.autographPosition
      );
      const canModifyAutographPosition =
        base.policy?.canModifyAutographPosition || false;
      const placementsChanged = signingsChanged(base.signings, signee.signings);
      if (emailChanged || positionChanged || placementsChanged) {
        await persistSigneeUpdate(
          documentId,
          doc,
          signee,
          (positionChanged && canModifyAutographPosition) ||
            (placementsChanged && canModifyAutographPosition)
        );
      }
    }

    // 4. Sign order — only when ordering or grouping actually changed. The
    // backend recreates every signee group when the sign order/grouping is
    // updated, which resets each group's quorum (requiredSignatures /
    // requiredApprovals) back to null — even for groups whose membership
    // didn't change. Snapshot the quorum-per-signee right before the call so
    // reconcileGroupQuorums() can restore it afterwards for groups the user
    // didn't explicitly edit this session.
    const orderedChanged = createStore.ordered !== orderedBaseline.value;
    if (createStore.ordered) {
      const desiredGroups = groupingPayload(draft);
      const baselineGroups = groupingPayload(baseline);
      if (
        orderedChanged ||
        JSON.stringify(desiredGroups) !== JSON.stringify(baselineGroups)
      ) {
        createStore.setQuorumSnapshot(doc.signeeGroups);
        await deepSignAPI.value.documentsSignOrder.update(
          documentId,
          desiredGroups
        );
      }
    } else if (orderedChanged) {
      createStore.setQuorumSnapshot(doc.signeeGroups);
      await deepSignAPI.value.documentsSignOrder.delete(documentId);
    }

    // 5. Commit the draft into the document and clear it so the working set
    // falls back to the committed document. Only refresh the sign-order context
    // label when the sign order actually changed (it's unaffected otherwise).
    doc.signeesOrdered = cloneDeep(draft);
    createStore.draftSigneesOrdered = null;
    orderedBaseline.value = createStore.ordered;
    // Rebase the snapshot onto the freshly committed signees (draft ids are now
    // real) so a subsequent Confirm without reopening diffs from here.
    initialSigneeIds.value = realSigneeIds(doc.signeesOrdered);

    await refreshSigneeGroupsAfterMembershipChange(
      addedSigneeIds,
      removedSigneeIds
    );

    return { addedSigneeIds, removedSigneeIds };
  }

  return {
    beginParticipantsDraft,
    discardParticipantsDraft,
    isParticipantsDraftDiscardable,
    reconcileParticipants
  };
}
